import assert from 'assert'
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import Header from '../../components/collections/Header'
import { CoverSpinning } from '../../components/elements'
import Modal from '../../components/modals/Modal'
import TagManager from '../../lib/tag-manager'
import { isValidCID } from '../../lib/utils'
import { ActionType } from '../../providers/action'
import { useAppState, useDispatcher } from '../../providers/store'
import getApi from '../../services'

interface FormInput {
    cid: string
}

interface INoticeModal {
    title: string
    message: string
    toggle(e: React.SyntheticEvent<HTMLButtonElement>): void
}

const NoticeModal: React.FC<INoticeModal> = ({ title, message, toggle }) => (
    <Modal>
        <div className="notice-modal-content abcs-container">
            <header>
                <h4>{title}</h4>
                <p>{message}</p>
            </header>

            <button type="button" className="abcs-button fluid primary" onClick={toggle}>
                รับทราบ
            </button>
        </div>
    </Modal>
)

const ConfirmationCIDCrossSell: React.FC = () => {
    const [loading, setLoading] = useState(false)
    const [notice, setNotice] = useState(false)
    const dispatch = useDispatcher()
    const appState = useAppState()
    const navigate = useNavigate()
    const {
        register,
        handleSubmit,
        formState: { errors },
        trigger,
    } = useForm<FormInput>()
    const cidField = register('cid', {
        required: {
            value: true,
            message: 'กรุณาระบุหมายเลขประจำตัวประชาชน',
        },
        validate: (value: string) => {
            if (process.env.DISABLED_REAL_CID_CHECKED === 'true') {
                return true
            }
            if (!isValidCID(value)) {
                return 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบและดำเนินการอีกครั้ง'
            }
            return true
        },
    })

    const onSubmit = async ({ cid }: FormInput) => {
        try {
            setLoading(true)
            const result = await getApi().ptpService.checkCID(cid)
            dispatch({ type: ActionType.SAVED_USER_INFO, payload: { user: result } })

            const { userId } = await window.liff.getProfile()

            assert(
                userId === result.uid,
                `Fraud detected!! ${window.liff.getAccessToken()} :: ${appState.user?.uid}`
            )

            const { accepted } = await getApi().pdpaService.revokeCrossSell({ uid: result.uid })
            dispatch({ type: ActionType.PDPA_SUBMITTED, payload: accepted })
        } catch (error) {
            setNotice(true)
            TagManager.popupEvent({ page: 'verification-cid-invalid', field: error.message })
        } finally {
            setLoading(false)
        }
    }

    useMemo(() => {
        TagManager.loadEvent({ page: 'verification-cid-cross-sell' })
    }, [])

    // NOTE: Fix memory leak
    useEffect(() => {
        if (appState.user) {
            navigate('/pdpa/done')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appState.user])

    return (
        <>
            {loading && <CoverSpinning />}
            {notice && (
                <NoticeModal
                    title="เกิดข้อผิดพลาด"
                    message="กรุณาลองใหม่อีกครั้ง"
                    toggle={() => setNotice(false)}
                />
            )}
            <div className="confirm-cid">
                <Header title="PDPA" messages={['ยังทำรายการไม่เสร็จ กรุณาทำรายการใหม่อีกครั้ง']} />
                <div className="abcs-container">
                    <section className="information">
                        <h5>
                            การยกเลิกการให้ความยินยอมเปิดเผยข้อมูลเพื่อวัตถุประสงค์ทางการตลาด
                            (cross-sell) ของแอป Money Thunder
                        </h5>
                        <p>กรุณากรอกข้อมูลเพื่อดำเนินการต่อ</p>
                    </section>
                    <div className={`abcs-input ${errors.cid && 'error'} fluid`}>
                        <label htmlFor="cid">กรอกหมายเลขบัตรประชาชน เพื่อยืนยันตัวตน</label>
                        <input
                            id="cid"
                            maxLength={13}
                            pattern="[0-9]*"
                            type="text"
                            {...cidField}
                            onBlur={(e) => {
                                cidField.onBlur(e)
                                trigger()
                            }}
                        />
                        {errors.cid && <small>{errors.cid.message}</small>}
                    </div>
                    <footer className="footer">
                        <button
                            className={`abcs-button primary  fluid ${errors.cid && 'disabled'}`}
                            type="submit"
                            onClick={handleSubmit(onSubmit)}
                        >
                            ต่อไป
                        </button>
                    </footer>
                </div>
            </div>
        </>
    )
}

export default ConfirmationCIDCrossSell
